import { ColumnDef } from '@tanstack/react-table'
import { useTranslations } from '@/hooks/useTranslations'
import { useMemo } from 'react'

import { UserDropdown, UserStatus } from '@/constants/user'
import { useLanguage } from '@/hooks'
import * as DateUtil from '@/utils/date'
import { formatTableNumber } from '@/utils/number'

import { ActionDropdown } from './ActionDropdown'
import { RoleModel } from '@/models/role/role'
import { RoleStatus } from '@/constants/role'
import { RoleStatusBadge } from './RoleStatusBadge'

type UseDatatableHeadersProps = {
  search: string
  onClickUserDropdownEvent: (eventName: UserDropdown, data: RoleModel) => void
}

export function useDatatableHeaders({ search, onClickUserDropdownEvent }: UseDatatableHeadersProps) {
  const t = useTranslations('admin.role_management.table')

  const { locale } = useLanguage()

  const columns = useMemo(() => {
    const columnsRender: (ColumnDef<RoleModel> & { sticky?: 'left' | 'right' })[] = [
      {
        id: 'no',
        header: t('no'),
        cell: (ctx) => (
          <div className="text-start">
            {formatTableNumber({
              index: ctx.row.index + 1,
              pageSize: ctx.table.getState().pagination.pageSize,
              pageIndex: ctx.table.getState().pagination.pageIndex,
            })}
          </div>
        ),
        size: 60,
        enableSorting: false,
        meta: {
          align: 'center',
        },
      },
      {
        id: 'name',
        header: t('name'),
        cell: (ctx) =>
          ctx.row.original?.name ?? "-",
        size: 200,
        enableSorting: false,
      },
      {
        id: 'description',
        header: t('description'),
        cell: (ctx) =>
          ctx.row.original?.description ?? "-",
        size: 200,
        enableSorting: false,
      },
      {
        id: 'count_role',
        header: t('role'),
        cell: (ctx) => ctx.row.original.permissions.length ?? "-",
        size: 100,
        enableSorting: false,
      },
      {
        id: 'max_drivice',
        header: t('max_drivice'),
        cell: (ctx) => ctx.row.original.max_drivice ?? "-",
        size: 100,
        enableSorting: false,
      },
      {
        id: 'status',
        header: t('status'),
        cell: (ctx) => <RoleStatusBadge status={ctx.row.original.status as UserStatus} />,
        size: 160,
        enableSorting: false,
      },
      {
        id: 'created_by',
        header: t('created_by'),
        cell: (ctx) => ctx.row.original.created_by.name ?? "-",
        size: 200,
        enableSorting: false,
      },
      {
        id: 'created_at',
        header: t('created_at'),
        cell: (ctx) => DateUtil.formatDate(ctx.row.original.created_at ?? "-", DateUtil.DateFormat.DATE_TIME, locale),
        size: 200,
        enableSorting: true,
      },
      {
        id: 'updated_by',
        header: t('updated_by'),
        cell: (ctx) => ctx.row.original?.updated_by?.name ?? "-",
        size: 200,
        enableSorting: false,
      },
      {
        id: 'updated_at',
        header: t('updated_at'),
        cell: (ctx) => DateUtil.formatDate(ctx.row.original?.updated_at ?? "-", DateUtil.DateFormat.DATE_TIME, locale),
        size: 200,
        enableSorting: true,
      },
      {
        id: 'action',
        header: '',
        cell: (ctx) => (
          <ActionDropdown
            isDefault={ctx.row.original.is_default}
            status={ctx.row.original.status as RoleStatus}
            onClickDropdownItem={(eventName) => onClickUserDropdownEvent(eventName, ctx.row.original)}
          />
        ),
        size: 60,
        enableSorting: false,
        sticky: 'right',
      },
    ]
    return columnsRender
  }, [locale, t, search, onClickUserDropdownEvent])

  return { columns }
}
